import type { DailyNutritionSummary, MealLogRow } from './mealLogCore'

export type SessionPerformanceTier = 'excellent' | 'solid' | 'partial' | 'minimal'

export type DebriefActionId = 'open_meals' | 'open_shopping' | 'connect_wearable' | 'sync_calendar' | 'rest'

export type DebriefAction = {
  id: DebriefActionId
  label: string
  primary?: boolean
}

export type SessionDebriefContext = {
  first_name: string | null
  block_title: string
  focus_subtitle: string
  day_number: number
  dietary_label: string
  set_progress_pct: number
  exercises_completed: number
  exercises_total: number
  performance_tier: SessionPerformanceTier
  session_rpe: number | null
  injury_status: string | null
  session_duration_minutes: number | null
  has_wearable: boolean
  calendar_sync_enabled: boolean
  recovery_score: number | null
  hrv_rmssd: number | null
  nutrition: DailyNutritionSummary | null
  due_meal: {
    block_name: string
    dish_name: string
    time_stamp: string
    logged: boolean
    overdue: boolean
  } | null
  shopping: {
    has_list: boolean
    items_outstanding: number
    items_depleted: number
  }
}

export type SessionDebrief = {
  headline: string
  coach_message: string
  performance_tier: SessionPerformanceTier
  actions: DebriefAction[]
  recovery_hint: string | null
  source: 'ai' | 'rules'
}

export function classifyPerformanceTier(setProgressPct: number, exercisesCompleted: number, exercisesTotal: number): SessionPerformanceTier {
  const completionRate = exercisesTotal > 0 ? exercisesCompleted / exercisesTotal : setProgressPct / 100
  const score = Math.round(setProgressPct * 0.6 + completionRate * 100 * 0.4)

  if (score >= 90) return 'excellent'
  if (score >= 70) return 'solid'
  if (score >= 40) return 'partial'
  return 'minimal'
}

function parseTimeToMinutes(timeStamp: string): number {
  const parts = timeStamp.split(':').map(Number)
  if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return 12 * 60
  return parts[0] * 60 + parts[1]
}

export function findDueMeal(
  meals: { block_name: string; dish_name: string; time_stamp: string; day_id: string }[],
  logs: MealLogRow[],
  dayId: string,
  nowMinutes: number,
): SessionDebriefContext['due_meal'] {
  const todayMeals = meals
    .filter((meal) => meal.day_id === dayId)
    .sort((a, b) => parseTimeToMinutes(a.time_stamp) - parseTimeToMinutes(b.time_stamp))

  if (todayMeals.length === 0) return null

  const unlogged = todayMeals.filter((meal) => {
    const entry = logs.find(
      (log) =>
        log.meal_block_name === meal.block_name &&
        (log.event_type === 'consumed' || log.event_type === 'skipped'),
    )
    return !entry
  })

  if (unlogged.length === 0) return null

  const overdue = unlogged.filter((meal) => parseTimeToMinutes(meal.time_stamp) <= nowMinutes)
  const candidate = overdue[overdue.length - 1] ?? unlogged[0]

  return {
    block_name: candidate.block_name,
    dish_name: candidate.dish_name,
    time_stamp: candidate.time_stamp,
    logged: false,
    overdue: overdue.length > 0,
  }
}

export function buildDebriefActions(context: SessionDebriefContext): DebriefAction[] {
  const actions: DebriefAction[] = []

  if (context.due_meal && !context.due_meal.logged) {
    actions.push({
      id: 'open_meals',
      label: context.due_meal.overdue
        ? `Log ${context.due_meal.block_name.toLowerCase()}`
        : `Open ${context.due_meal.dish_name}`,
      primary: true,
    })
  } else if ((context.nutrition?.slots_unlogged ?? 0) > 0) {
    actions.push({
      id: 'open_meals',
      label: 'Log today\'s meals',
      primary: true,
    })
  }

  if (context.shopping.has_list && context.shopping.items_outstanding > 0) {
    actions.push({
      id: 'open_shopping',
      label: `Shopping · ${context.shopping.items_outstanding} item${context.shopping.items_outstanding === 1 ? '' : 's'} left`,
      primary: actions.length === 0,
    })
  }

  if (!context.has_wearable) {
    actions.push({ id: 'connect_wearable', label: 'Connect wearable' })
  } else if (!context.calendar_sync_enabled) {
    actions.push({ id: 'sync_calendar', label: 'Sync calendar' })
  }

  if (context.performance_tier === 'minimal' || context.performance_tier === 'partial') {
    actions.push({ id: 'rest', label: 'Prioritize recovery tonight' })
  }

  return actions.slice(0, 3)
}

function performanceOpener(tier: SessionPerformanceTier, blockTitle: string): string {
  switch (tier) {
    case 'excellent':
      return `Strong work on ${blockTitle}. You hit the prescribed work — that's the standard.`
    case 'solid':
      return `Solid session on ${blockTitle}. You moved the needle even if every set wasn't perfect.`
    case 'partial':
      return `Partial completion on ${blockTitle}. The stimulus landed, but today wasn't full volume — recovery matters more now.`
    case 'minimal':
      return `Light touch on ${blockTitle} today. That's data, not failure — we protect recovery and rebuild intent next time.`
  }
}

function nutritionSentence(context: SessionDebriefContext): string | null {
  const { due_meal, nutrition } = context
  if (due_meal && !due_meal.logged) {
    if (due_meal.overdue) {
      return `Your ${due_meal.block_name.toLowerCase()} (${due_meal.dish_name}) isn't logged yet — after training, that window is when your body partitions nutrients best.`
    }
    return `${due_meal.block_name} is coming up (${due_meal.dish_name}). Logging what you actually eat keeps Kilo aligned with your plan.`
  }

  if (nutrition && nutrition.slots_logged === 0) {
    return `Nothing logged in meals today yet — Kilo can't tell if you're fueling this session or flying blind.`
  }

  const proteinGap = (nutrition?.planned.protein_g ?? 0) - (nutrition?.actual.protein_g ?? 0)
  if (nutrition && nutrition.slots_logged > 0 && proteinGap >= 25) {
    return `You're ${proteinGap}g under today's protein plan so far — a logged meal in the next couple hours closes the gap.`
  }

  if (nutrition && nutrition.slots_substituted > 0) {
    return `You've swapped ${nutrition.slots_substituted} meal${nutrition.slots_substituted === 1 ? '' : 's'} off-plan today — logging the substitute keeps recovery advice honest.`
  }

  if (nutrition && nutrition.adherence_pct != null && nutrition.adherence_pct >= 80) {
    return `Nutrition is tracking well today (${nutrition.adherence_pct}% on plan) — stay with that rhythm tonight.`
  }

  return null
}

function shoppingSentence(context: SessionDebriefContext): string | null {
  if (!context.shopping.has_list) {
    return null
  }
  if (context.shopping.items_outstanding === 0) {
    return `Your shopping list is clear — no procurement run needed right now.`
  }
  return `${context.shopping.items_outstanding} item${context.shopping.items_outstanding === 1 ? '' : 's'} still on your list if you need to restock this week.`
}

function rpeSentence(context: SessionDebriefContext): string | null {
  if (context.session_rpe == null) return null
  if (context.injury_status === 'ACUTE') {
    return `You flagged acute discomfort (RPE ${context.session_rpe}/10) — tomorrow's plan will bias toward recovery until that clears.`
  }
  if (context.injury_status === 'DISCOMFORT') {
    return `You noted discomfort at RPE ${context.session_rpe}/10 — I'll keep load conservative until patterns stabilize.`
  }
  if (context.session_rpe >= 9 && context.performance_tier !== 'excellent') {
    return `RPE ${context.session_rpe}/10 for partial volume — the effort outran the work. Extra sleep and food beat another hard push tomorrow.`
  }
  if (context.session_rpe <= 5 && context.performance_tier === 'excellent') {
    return `RPE ${context.session_rpe}/10 with full volume — you've got headroom when recovery allows.`
  }
  return null
}

function recoveryHint(context: SessionDebriefContext): string | null {
  if (context.recovery_score != null && context.recovery_score < 45) {
    return `Recovery score ${context.recovery_score}/100 — prioritize sleep and hydration tonight.`
  }
  if (context.hrv_rmssd != null && context.hrv_rmssd < 35) {
    return `HRV is suppressed (${Math.round(context.hrv_rmssd)} ms) — protect the nervous system tonight.`
  }
  if (context.has_wearable) {
    return `Wearable loop active — I'll use overnight HRV and sleep to tune tomorrow's load.`
  }
  return null
}

export function buildRuleBasedDebrief(context: SessionDebriefContext): SessionDebrief {
  const parts = [
    performanceOpener(context.performance_tier, context.focus_subtitle || context.block_title),
    rpeSentence(context),
    nutritionSentence(context),
    context.performance_tier === 'excellent' || context.performance_tier === 'solid'
      ? shoppingSentence(context)
      : nutritionSentence(context) ? null : shoppingSentence(context),
  ].filter(Boolean) as string[]

  const headlineMap: Record<SessionPerformanceTier, string> = {
    excellent: 'Session locked in',
    solid: 'Good work today',
    partial: 'Partial session — recover smart',
    minimal: 'Light day logged',
  }

  return {
    headline: headlineMap[context.performance_tier],
    coach_message: parts.join(' '),
    performance_tier: context.performance_tier,
    actions: buildDebriefActions(context),
    recovery_hint: recoveryHint(context),
    source: 'rules',
  }
}
